import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { 
  Database, 
  Download, 
  Upload, 
  Trash2, 
  FileText, 
  Calendar, 
  AlertTriangle,
  CheckCircle,
  Clock,
  HardDrive,
  RefreshCw,
  Mail,
  Users,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const DataBackup = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [sections, setSections] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [includeUserData, setIncludeUserData] = useState(false);
  const [keepAccounts, setKeepAccounts] = useState(true);
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [processingBackup, setProcessingBackup] = useState(false);
  const [processingDelete, setProcessingDelete] = useState(false);
  const [expandedBackup, setExpandedBackup] = useState(null);

  useEffect(() => {
    fetchSections();
    fetchBackups();
    
    // Set default dates (last 30 days)
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    setDateTo(today.toISOString().split('T')[0]);
    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSections = async () => {
    try {
      const res = await api.get('/data-backup/sections');
      setSections(res.data.sections);
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل تحميل الأقسام' : 'Failed to load sections');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    try {
      const res = await api.get('/data-backup/list');
      setBackups(res.data.backups);
    } catch (error) {
      console.error('Failed to fetch backups:', error);
    }
  };

  const toggleSection = (sectionId) => {
    setSelectedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(s => s !== sectionId)
        : [...prev, sectionId]
    );
  };

  const selectAllSections = () => {
    if (selectedSections.length === sections.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(sections.map(s => s.id));
    }
  };

  const handlePreview = async () => {
    if (selectedSections.length === 0) {
      toast.error(language === 'ar' ? 'يرجى اختيار قسم واحد على الأقل' : 'Please select at least one section');
      return;
    }

    try {
      setProcessingBackup(true);
      const res = await api.post('/data-backup/preview', {
        sections: selectedSections,
        date_from: dateFrom,
        date_to: dateTo,
        include_user_data: includeUserData
      });
      setPreviewData(res.data);
      setShowPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل معاينة البيانات' : 'Failed to preview data'));
    } finally {
      setProcessingBackup(false);
    }
  };

  const handleCreateBackup = async () => {
    if (selectedSections.length === 0) {
      toast.error(language === 'ar' ? 'يرجى اختيار قسم واحد على الأقل' : 'Please select at least one section');
      return;
    }

    try {
      setProcessingBackup(true);
      const res = await api.post('/data-backup/create', {
        sections: selectedSections,
        date_from: dateFrom,
        date_to: dateTo,
        include_user_data: includeUserData,
        keep_accounts: keepAccounts
      });
      toast.success(language === 'ar' ? 'تم إنشاء النسخة الاحتياطية بنجاح' : 'Backup created successfully');
      setShowPreview(false);
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل إنشاء النسخة الاحتياطية' : 'Failed to create backup'));
    } finally {
      setProcessingBackup(false);
    }
  };

  const handleDeleteData = async () => {
    if (selectedSections.length === 0) {
      toast.error(language === 'ar' ? 'يرجى اختيار قسم واحد على الأقل' : 'Please select at least one section');
      return;
    }

    try {
      setProcessingDelete(true);
      const res = await api.post('/data-backup/delete-data', {
        sections: selectedSections,
        date_from: dateFrom,
        date_to: dateTo,
        confirm_delete: true,
        keep_accounts: keepAccounts
      });
      toast.success(
        language === 'ar' 
          ? `تم حذف ${res.data.total_deleted} عنصر وإشعار ${res.data.notified_users} مستخدم` 
          : `Deleted ${res.data.total_deleted} items and notified ${res.data.notified_users} users`
      );
      setShowDeleteConfirm(false);
      fetchSections();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل حذف البيانات' : 'Failed to delete data'));
    } finally {
      setProcessingDelete(false);
    }
  };

  const handleDownloadPDF = async (backupId) => {
    try {
      const res = await api.post(`/data-backup/${backupId}/generate-pdf?send_email=true`);
      
      // Download PDF
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${res.data.pdf_base64}`;
      link.download = res.data.filename;
      link.click();
      
      if (res.data.email_sent) {
        toast.success(language === 'ar' ? 'تم إرسال PDF إلى بريدك الإلكتروني' : 'PDF sent to your email');
      } else {
        toast.success(language === 'ar' ? 'تم تحميل PDF' : 'PDF downloaded');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل إنشاء PDF' : 'Failed to generate PDF'));
    }
  };

  const handleRestoreBackup = async (backupId, sections) => {
    try {
      const res = await api.post(`/data-backup/${backupId}/restore`, {
        backup_id: backupId,
        sections: sections
      });
      toast.success(
        language === 'ar' 
          ? `تم استعادة ${res.data.total_restored} عنصر بنجاح` 
          : `Restored ${res.data.total_restored} items successfully`
      );
      fetchSections();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل استعادة البيانات' : 'Failed to restore data'));
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذه النسخة الاحتياطية؟' : 'Are you sure you want to delete this backup?')) {
      return;
    }

    try {
      await api.delete(`/data-backup/${backupId}`);
      toast.success(language === 'ar' ? 'تم حذف النسخة الاحتياطية' : 'Backup deleted');
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل حذف النسخة' : 'Failed to delete backup'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return language === 'ar' 
      ? date.toLocaleDateString('ar-AE', { year: 'numeric', month: 'short', day: 'numeric' })
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
            <Database className="w-6 h-6 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {language === 'ar' ? 'النسخ الاحتياطي والاستعادة' : 'Backup & Restore'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'ar' ? 'إدارة بيانات النظام والنسخ الاحتياطي' : 'Manage system data and backups'}
            </p>
          </div>
        </div>

        {/* Create Backup Section */}
        <div className="bg-card border border-white/5 rounded-xl p-6 space-y-6">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            {language === 'ar' ? 'إنشاء نسخة احتياطية جديدة' : 'Create New Backup'}
          </h2>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'ar' ? 'من تاريخ' : 'From Date'}
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-secondary/50 border-white/10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'ar' ? 'إلى تاريخ' : 'To Date'}
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-secondary/50 border-white/10"
              />
            </div>
          </div>

          {/* Section Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium">
                {language === 'ar' ? 'اختر الأقسام' : 'Select Sections'}
              </label>
              <Button
                variant="ghost"
                size="sm"
                onClick={selectAllSections}
                className="text-primary"
              >
                {selectedSections.length === sections.length 
                  ? (language === 'ar' ? 'إلغاء الكل' : 'Deselect All')
                  : (language === 'ar' ? 'تحديد الكل' : 'Select All')
                }
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {sections.map(section => (
                <div
                  key={section.id}
                  onClick={() => toggleSection(section.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedSections.includes(section.id)
                      ? 'bg-primary/20 border-primary'
                      : 'bg-secondary/30 border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedSections.includes(section.id)}
                      onCheckedChange={() => toggleSection(section.id)}
                    />
                    <span className="text-sm font-medium">{section.label}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {section.document_count} {language === 'ar' ? 'عنصر' : 'items'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={includeUserData}
                onCheckedChange={setIncludeUserData}
              />
              <span className="text-sm">
                {language === 'ar' ? 'تضمين بيانات المستخدمين والحلاقين' : 'Include user and barber data'}
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={keepAccounts}
                onCheckedChange={setKeepAccounts}
              />
              <span className="text-sm">
                {language === 'ar' ? 'الاحتفاظ بالحسابات عند الحذف' : 'Keep accounts when deleting'}
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handlePreview}
              disabled={processingBackup || selectedSections.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Eye className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'معاينة البيانات' : 'Preview Data'}
            </Button>
            <Button
              onClick={handleCreateBackup}
              disabled={processingBackup || selectedSections.length === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {processingBackup ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {language === 'ar' ? 'إنشاء نسخة احتياطية' : 'Create Backup'}
            </Button>
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={processingDelete || selectedSections.length === 0}
              variant="destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {language === 'ar' ? 'حذف البيانات وإعادة التعيين' : 'Delete Data & Reset'}
            </Button>
          </div>
        </div>

        {/* Existing Backups */}
        <div className="bg-card border border-white/5 rounded-xl p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5" />
            {language === 'ar' ? 'النسخ الاحتياطية السابقة' : 'Previous Backups'}
          </h2>

          {backups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {language === 'ar' ? 'لا توجد نسخ احتياطية' : 'No backups found'}
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map(backup => (
                <div key={backup.id} className="border border-white/10 rounded-lg overflow-hidden">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                    onClick={() => setExpandedBackup(expandedBackup === backup.id ? null : backup.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Database className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">
                          {formatDate(backup.date_from)} - {formatDate(backup.date_to)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {backup.total_documents} {language === 'ar' ? 'عنصر' : 'items'} • {formatSize(backup.size_bytes)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(backup.created_at)}
                      </span>
                      {expandedBackup === backup.id ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </div>
                  </div>

                  {expandedBackup === backup.id && (
                    <div className="p-4 border-t border-white/10 bg-secondary/20">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-muted-foreground">
                            {language === 'ar' ? 'تم الإنشاء بواسطة' : 'Created by'}
                          </div>
                          <div className="font-medium">{backup.created_by_name}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">
                            {language === 'ar' ? 'الأقسام' : 'Sections'}
                          </div>
                          <div className="font-medium">{backup.sections?.length || 0}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">
                            {language === 'ar' ? 'الحجم' : 'Size'}
                          </div>
                          <div className="font-medium">{formatSize(backup.size_bytes)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">
                            {language === 'ar' ? 'الحالة' : 'Status'}
                          </div>
                          <div className="flex items-center gap-1 text-green-500">
                            <CheckCircle className="w-4 h-4" />
                            {language === 'ar' ? 'مكتمل' : 'Completed'}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleDownloadPDF(backup.id)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          {language === 'ar' ? 'تحميل PDF' : 'Download PDF'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleRestoreBackup(backup.id, backup.sections)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          {language === 'ar' ? 'استعادة' : 'Restore'}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteBackup(backup.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          {language === 'ar' ? 'حذف' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? 'معاينة البيانات' : 'Data Preview'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? `من ${previewData?.date_from} إلى ${previewData?.date_to}`
                : `From ${previewData?.date_from} to ${previewData?.date_to}`
              }
            </DialogDescription>
          </DialogHeader>

          {previewData && (
            <div className="space-y-4">
              <div className="bg-secondary/30 rounded-lg p-4">
                <div className="text-lg font-semibold mb-2">
                  {language === 'ar' ? 'إجمالي العناصر:' : 'Total Items:'} {previewData.total_documents}
                </div>
              </div>

              {previewData.sections?.map(section => (
                <div key={section.section} className="border border-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{section.label}</span>
                    <span className="text-sm text-muted-foreground">
                      {section.document_count} {language === 'ar' ? 'عنصر' : 'items'}
                    </span>
                  </div>
                  {section.sample?.length > 0 && (
                    <div className="text-xs text-muted-foreground bg-secondary/30 rounded p-2 max-h-20 overflow-y-auto">
                      {JSON.stringify(section.sample[0], null, 2).substring(0, 200)}...
                    </div>
                  )}
                </div>
              ))}

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowPreview(false)}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button onClick={handleCreateBackup} disabled={processingBackup}>
                  {processingBackup ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  {language === 'ar' ? 'إنشاء النسخة الاحتياطية' : 'Create Backup'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              {language === 'ar' ? 'تأكيد الحذف وإعادة التعيين' : 'Confirm Delete & Reset'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'هذا الإجراء لا يمكن التراجع عنه! سيتم حذف جميع البيانات المحددة وإرسال إشعار لجميع المستخدمين.'
                : 'This action cannot be undone! All selected data will be deleted and all users will be notified.'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="font-medium text-red-400 mb-2">
                {language === 'ar' ? 'سيتم حذف:' : 'Will be deleted:'}
              </div>
              <ul className="text-sm space-y-1">
                {selectedSections.map(sectionId => {
                  const section = sections.find(s => s.id === sectionId);
                  return (
                    <li key={sectionId} className="flex items-center gap-2">
                      <Trash2 className="w-3 h-3 text-red-400" />
                      {section?.label} ({section?.document_count} {language === 'ar' ? 'عنصر' : 'items'})
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-400">
                <Mail className="w-4 h-4" />
                <span className="font-medium">
                  {language === 'ar' ? 'سيتم إشعار جميع المستخدمين' : 'All users will be notified'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {language === 'ar' 
                  ? 'سيتم إرسال بريد إلكتروني ورسالة واتساب لجميع المستخدمين'
                  : 'Email and WhatsApp notifications will be sent to all users'
                }
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteData}
                disabled={processingDelete}
              >
                {processingDelete ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                {language === 'ar' ? 'نعم، احذف وأعد التعيين' : 'Yes, Delete & Reset'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataBackup;
